import styles from './ccf-text-editor.module.scss';
import { Editor } from 'react-draft-wysiwyg';
import {
  ContentState,
  EditorState,
  Modifier,
  convertToRaw,
  convertFromHTML,
  BlockMapBuilder,
  CompositeDecorator,
  AtomicBlockUtils } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CcfUploadIcon,
  CcfEmojiIcon,
  CcfImageIcon,
  CcfGifIcon,
  CcfButton,
  CcfFlashIcon,
  CcfCarousel,
  CcfTypography,
  CcfSendIcon,
  CcfInsertIcon,
  CcfTooltip,
  CcfSparklesIcon,
  CcfCopilotLikeIcon,
  CcfCopilotDislikeIcon,
  CcfCopilotLikedIcon,
  CcfCopilotDislikedIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  getActiveInteraction,
  getCopilotConfig,
  getIsInsert,
  getIsResponseInserted,
  getIsResponseSent,
  getIsSent,
  getMessageList,
  getNextBestResponses,
  getNextBestResponsesObjectId,
  getSentBestResponse,
  setIsInsertClicked,
  setIsSentClicked,
} from '@nice-ccf/ui-components';
import {
  DigitalChannelStatus,
  DigitalChannelType,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';
import {
  getSentQReply,
  updateSentQReply,
} from '../ccf-app-space/ccf-app-space.slice';
import {
  getActiveInteractionID,
  updateAppSpaceTab,
  getContactElevationDetails,
  updateTwitterAgentReply,
  updateActiveContact,
} from '../ccf-inbox/ccf-inbox.slice';
import { AppSpace } from '../../enums/app-space-tabs';
import { CancelOutlined, Send } from '@mui/icons-material';
import {
  getInteractionMsgCount,
  interactionSliceActions,
} from '../ccf-interaction-workspace/ccf-interaction-space.slice';
import { Box, Button, IconButton, Theme, useTheme } from '@mui/material';
import ccfEditorStyles from './ccf-text-editor-styles';

import {
  FormatTextdirectionLToR,
  FormatTextdirectionRToL,
} from '@mui/icons-material';
import { API_BASE_URL, callApi, PROCESS_UTTERANCE } from '../ccf-app-space/ccf-copilot/ccf-copilot-api';
import { v5 as uuidv5, NIL as namespaceNIL } from 'uuid';

/* eslint-disable-next-line */
export interface CcfTextEditorProps {
  getTextCB?: any;
}

/**
 * Component used for text editor
 * @param props - CcfTextEditorProps
 * @example <CcfTextEditor />
 * @returns
 */
export function CcfTextEditor(props: CcfTextEditorProps) {
  const [translate] = useTranslator();
  const [currentVal, setCurrentVal] = useState('');
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [data, setData] = useState('');
  const dispatch = useDispatch();
  const interactionMSgCount = useSelector(getInteractionMsgCount);
  const selectedCallContact = useSelector(activeContactSelector);
  const activecontact = useSelector(activeContactSelector);
  const contactElevatedDetails = useSelector(getContactElevationDetails);
  const activeInteraction = useSelector(getActiveInteraction);
  const { setInteractionMsgCount } = interactionSliceActions;
  const activecontactId = activecontact?.id || '201251606723';
  const sentNextBestResponse = useSelector(
    getSentBestResponse(activecontactId)
  );
  const isNextBestResponseInserted: boolean | undefined = useSelector(
    getIsResponseInserted(activecontactId)
  );
  const isNextBestResponseSent: boolean | undefined = useSelector(
    getIsResponseSent(activecontactId)
  );
  const editorRef = useRef<any>('');
  const isEnabled =
    activeInteraction?.hasAbilityToQuoteMessage &&
    (selectedCallContact?.contactStatus === VoiceContactStatus.ACTIVE ||
      selectedCallContact?.contactStatus === DigitalChannelStatus.OPEN);
  const theme = useTheme();
  const classes = ccfEditorStyles(theme);
  const [isResponseVisible, setIsResponseVisible] = useState(true);
  const isSend = useSelector(getIsSent);
  const isInsert = useSelector(getIsInsert);
  // const activeContactId = useSelector(getActiveInteractionID);
  const activeInteractionID: string = useSelector(getActiveInteractionID) || '';
  // const copilotConfigurations = useSelector(getCopilotConfig);
  const copilotConfigurations = JSON.parse(localStorage.getItem('agentAssistAppConfig') as string) || {};
  const index = copilotConfigurations?.path ?? 'custom';
  const isLiveCopilotEnabled = activecontact?.copilotSource === 'Live LLM';
  const copilotNbrsFromLLM = useSelector(
    getNextBestResponses(`${activecontact?.id}`)
  );
  // const copilotNbrsFromLLM = useSelector(getNextBestResponses(`201251606723`));
  const [nbrCardIndex, setNbrCardIndex] = useState(0);
  const primaryIconColor = theme?.palette?.primary?.main;
  const currentGetNextBestResponsesObjectId= useSelector(getNextBestResponsesObjectId(`${activecontact?.id}`));
  const [oldNBRId, setOldNBRId] = useState(currentGetNextBestResponsesObjectId);
  const namespace = uuidv5('namespace', namespaceNIL);
  const [isLikeClicked, setIsLikeClicked] = useState(false);
  const [isDisLikeClicked, setIsDisLikeClicked] = useState(false);
  const [shouldAddImage, setShouldAddImage] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const agentName = activecontact?.customerChatReplyJsonData !== undefined ? activecontact?.customerChatReplyJsonData[0]?.agentName?.split(' ')[0] : 'Agent';
  const [localConfiguration, setLocalConfiguration] = useState(copilotConfigurations);
  const locale = localConfiguration?.language;

  useEffect(()=> {
    if(oldNBRId !== currentGetNextBestResponsesObjectId ){
    setIsResponseVisible(true);
    setIsLikeClicked(false);
    setIsDisLikeClicked(false);
    setOldNBRId(currentGetNextBestResponsesObjectId)
    }
    else {
      setIsResponseVisible(false);
    }
  },[currentGetNextBestResponsesObjectId])

  useEffect(() => {
    if (shouldAddImage && imageUrl) {
      insertImage(imageUrl);
      setShouldAddImage(false);
      editorRef.current.focusEditor();
    }
  }, [shouldAddImage, imageUrl]);


  // eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
  const insertImage = (url: any) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'IMAGE',
      'IMMUTABLE',
      { src: url, height: '72px', width: '72px', alignment: 'left'}
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      editorState,
      entityKey,
      ' '
    );
    setEditorState(EditorState.forceSelection(newEditorState, newEditorState.getCurrentContent().getSelectionAfter()));
  };


  // eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
  const uploadImageCallback = (file: any) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return new Promise((resolve, reject) => {
      resolve({ data: { link: url } });
    });
  };

  /**
   * Function on editor state change
   * @param editorState - any
   * @example - onEditorStateChange()
   */
  const onEditorStateChange = (editorState: any) => {
    const blocks = convertToRaw(editorState.getCurrentContent()).blocks;
    const value = blocks
      .map((block) => (!block.text.trim() && '\n') || block.text)
      .join('\n');
    setData(value);
    setEditorState(editorState);
  };

  const toolBarOptions = {
    options: ['inline', 'textAlign'],
    inline: {
      options: ['bold', 'italic', 'underline'],
      bold: { className: 'noBorder' },
      italic: { className: 'noBorder' },
      underline: { className: 'noBorder' },
    },
    textAlign: {
      options: ['left', 'center', 'right'],
      left: { className: 'noBorder' },
      center: { className: 'noBorder' },
      right: { className: 'noBorder' },
    },
  };

  const noToolBarOptions = {
    options: [],
    image: {
      uploadCallback: uploadImageCallback,
      previewImage: true,
      alt: { present: true, mandatory: false },
    },
  };

  /**
   *
   */
  const shiftEnterFunction = useCallback(
    (e: any) => {
      if (e.key === 'Enter' && e.shiftKey && data) {
        sendMessage();
      }
    },
    [data]
  );

  /**
   *@example function to insert dynamic text to editor
   */
   const insertText = (text: any, editorState: any) => {
    const currentContent = editorState.getCurrentContent(),
      currentSelection = editorState.getSelection();

    const newContent = Modifier.replaceText(
      currentContent,
      currentSelection,
      text
    );
    const newEditorState = EditorState.push(
      editorState,
      newContent,
      'insert-characters'
    );
    return EditorState.forceSelection(
      newEditorState,
      newContent.getSelectionAfter()
    );
  };

  useEffect(() => {
    if (
      selectedCallContact?.isElevated &&
      selectedCallContact?.copilotAssist === 'Yes' &&
      contactElevatedDetails?.has('sms')
    ) {
      setTimeout(() => {
        setEditorState(
          insertText(contactElevatedDetails.get('sms')?.message[0], editorState)
        );
        editorRef.current?.focusEditor();
        setData(contactElevatedDetails.get('sms')?.message[0]);
      }, 500);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', shiftEnterFunction, false);
    return () => {
      document.removeEventListener('keydown', shiftEnterFunction, false);
    };
  }, [shiftEnterFunction]);

  useEffect(() => {
    if (isSend.clicked) {
      sendMessage(isSend.message);
      // setTimeout(() => {
      dispatch(setIsSentClicked({ clicked: false, message: '' }));
      // }, 0);
    }
    if (isInsert.clicked) {
      insertNBRHandler(isInsert.message);
      // setTimeout(() => {
      dispatch(setIsInsertClicked({ clicked: false, message: '' }));
      // }, 0);
    }
  }, [isSend, isInsert]);

  useEffect(() => {
    if (isNextBestResponseSent) {
      sendMessage(sentNextBestResponse);
    }
  }, [isNextBestResponseSent]);

  useEffect(() => {
    if (isNextBestResponseInserted) {
      insertNBRHandler(sentNextBestResponse);
    }
  }, [isNextBestResponseInserted]);

  /**
   * Function to send message
   * @example - sendMessage()
   */
  const sendMessage = (NBRMessage = data) => {
    if ((data.trim() || NBRMessage.trim()) && imageUrl === '') {
      //dispatch(messageStateActions.messageQueue({value: data, id: count, isClientMessage: false, time: 'now', conferenceMsg: false}));
      props.getTextCB(NBRMessage);
      let interactionMSgCountCopy: any = [];

      const interactionIndex = interactionMSgCount.findIndex(
        (contact: any) => contact.interactionID === selectedCallContact?.id
      );
      const isInteractionPresent = interactionMSgCount.find(
        (contact: any) => contact.interactionID === selectedCallContact?.id
      );
      const writableisInteractionPresent = Object.assign(
        {},
        isInteractionPresent
      );
      if (interactionIndex >= 0 && isInteractionPresent) {
        writableisInteractionPresent.userChatMsgCount =
          isInteractionPresent.userChatMsgCount + 1;
        //interactionMSgCountCopy[interactionIndex] = writableisInteractionPresent;
        interactionMSgCountCopy = interactionMSgCount.map(
          (interaction: any) => {
            if (
              interaction.interactionID ===
              writableisInteractionPresent.interactionID
            ) {
              return {
                ...interaction,
                userChatMsgCount: isInteractionPresent.userChatMsgCount + 1,
              };
            }
            return interaction;
          }
        );
      } else {
        interactionMSgCountCopy = [
          ...interactionMSgCount,
          {
            interactionID: selectedCallContact ? selectedCallContact?.id : '',
            userChatMsgCount: 1,
          },
        ];
      }
      dispatch(setInteractionMsgCount(interactionMSgCountCopy));
      setData('');
      setEditorState(EditorState.createEmpty());
    }
    if (
      selectedCallContact?.isElevated &&
      selectedCallContact?.channel === DigitalChannelType.CHAT &&
      selectedCallContact?.copilotAssist === 'Yes' &&
      selectedCallContact?.copilotSource === 'JSON' &&
      contactElevatedDetails.has('sms')
    ) {
      const contactElevatedDetailsCopy = new Map();
      contactElevatedDetails.forEach(function (value: any, key: any) {
        if (key !== 'sms') {
          contactElevatedDetailsCopy.set(key, value);
        }
      });
      // const contactElevatedDetailsCopy = Object.assign(contactElevatedDetails);
      // contactElevatedDetailsCopy.delete('sms');
     
      dispatch(
        updateActiveContact({
          id: selectedCallContact?.id || '',
          updates: { elevationDetails: contactElevatedDetailsCopy },
        })
      );
    }
    if(imageUrl !== ''){
      const imagesResponse = [{
        type: 'image',
        isItemSent: true,
        isItemSelected: true,
        imgSrc: imageUrl,
        id: 1
      }];
      const replyMessage = {
        messageId: new Date().toString(),
        postId:
          'chat_1359751597379620865_b81029d6-f53b-406b-8bef-314d1eabb2341',
        isOwn: true,
        createdAt: formatMsgTime(),
        messageContent: {
          payload: {
            text: NBRMessage.trim(),
            linkContent: [],
            imageContent: imagesResponse ?? [],
            processContent: [],
          },
        },
        reactionStatistics: {
          likes: 0,
        },
        authorUser: {
          firstName: agentName,
          surname:'',
          emailAddress: 'customer.service@delta.com',
          loginUsername: '',
        },
        attachments: [],
        conferenceMsg: false,
        summaryCardName: '',
      };
      // dispatch(updateFileToBeUploaded(dataTransfer.files));
      dispatch(
        updateTwitterAgentReply({
          interactionId: activeInteractionID,
          replyMessage,
        })
      );
      setData('');
      setEditorState(EditorState.createEmpty());
    }
    editorRef.current.focusEditor();
    if (
      selectedCallContact?.channel === DigitalChannelType.CHAT &&
      selectedCallContact?.copilotAssist === 'Yes' &&
      selectedCallContact?.copilotSource === 'Live LLM' &&
      NBRMessage.trim()
    ) {
      const requestBody = {    
        "contactId": activecontact?.id,
        "busNo": 12345678,
        "tenantId": "12345678",
        "participantId": "agent",
        "messageBody": NBRMessage,
        "messageMetaData": {
            "completeUtterance": true
        },
        "transcriptionEventId": uuidv5(NBRMessage, namespace),
        "threshold_sales": copilotConfigurations?.threshold_sales ?? 0.75,
        "threshold_compliance": copilotConfigurations?.threshold_compliance ?? 0.75,
        "agentAssistAppConfig": {
            "language": locale?.toLowerCase(),
            "generativeResponseTone": copilotConfigurations?.generativeResponseTone ?? 'Formal',
            "channels": [
                "digital",
                "voice"
            ],
            "transcriptionProfileName": "google-test",
            "enableSentiment": copilotConfigurations?.enableSentiment ??  true,
            "sentimentResponseLength": copilotConfigurations?.sentimentResponseLength ?? 20,
            "realTimeSummary": copilotConfigurations?.realTimeSummary ?? true,
            "realTimeSummaryResponseLength": copilotConfigurations?.realTimeSummaryResponseLength ?? 25,
            "finalSummary": copilotConfigurations?.finalSummary ?? true,
            "finalSummaryResponseLength": copilotConfigurations?.finalSummaryResponseLength ?? 65,
            "behavioralGuidance": true,
            "behavioralGuidanceResponseLength": 10,
            "behevioralGuidanceNumberOfResponse": 3,
            "enableExpertCopilotIntegration": true,
            "expertCopilotURL": copilotConfigurations?.expertCopilotURL ?? '',
            "expertCopilotClientKey": copilotConfigurations?.expertCopilotClientKey ?? '',
            "expertCopilotClientSecret": copilotConfigurations?.expertCopilotClientSecret ?? '',
            "path": copilotConfigurations?.path ??'',
            "botPersona": copilotConfigurations?.botPersona ?? '',
            "kBAnswersPath": copilotConfigurations?.kBAnswersPath ??'',
            "salesOffersPath": copilotConfigurations?.salesOffersPath ??'',
            "compliancePath": copilotConfigurations?.compliancePath ??'',
            "nextBestResponse": copilotConfigurations?.nextBestResponse ??true,
            "nextBestResponseLength": copilotConfigurations?.nextBestResponseLength ?? 20,
            "nextBestNumberOfResponse": copilotConfigurations?.nextBestNumberOfResponse ?? 2,
            "knowledgeBaseArticles": copilotConfigurations?.knowledgeBaseArticles ?? true,
            "knowledgeBaseArticlesResponseLength": copilotConfigurations?.knowledgeBaseArticlesResponseLength ?? 65,
            "knowledgeBaseWebLinks": copilotConfigurations?.knowledgeBaseWebLinks ?? true,
            "knowledgeBaseMaximumWebLinks": copilotConfigurations?.knowledgeBaseMaximumWebLinks ?? 3,
            "knowledgeBaseImages": copilotConfigurations?.knowledgeBaseImages ?? true,
            "knowledgeBaseMaximumImages": copilotConfigurations?.knowledgeBaseMaximumImages ?? 3,
            "knowledgeBaseProcessSteps": copilotConfigurations?.knowledgeBaseProcessSteps ?? true,
            "knowledgeBaseMaximumSteps":copilotConfigurations?.knowledgeBaseMaximumSteps ?? 10,
            "threshold": copilotConfigurations?.threshold ?? 0.75,
            "vendorId": "agentCopilot",
            "agentAssistId": "Ved_acp_test123",
            "scriptParams": "",
            "backAndForthLiveConvo": copilotConfigurations?.backAndForthLiveConvo ?? false,
            "backAndForthLiveConvoLength": copilotConfigurations?.backAndForthLiveConvoLength ?? 4,
        },
        "messageIsCompletedUtterance": true,
        "agentId": 1898,
        "mediaType": activecontact?.media === 'Voice'? 'Voice': 'Digital',
        "totalAudioChunksInMilliSeconds": 2040
      };
      new Promise<void>((resolve, reject) => {
        callApi(
          API_BASE_URL + PROCESS_UTTERANCE,
          'POST',
          requestBody,
          dispatch
        )
          .then((response: any) => {
            // processApiResponse(response.data, dispatch);
            resolve();
          })
          .catch((error: any) => {
            console.error(
              'There was a problem with the fetch operation from editor:',
              error
            );
            reject(error);
          });
      });
      setIsResponseVisible(false);
    }
    document.getElementById('bodyContainer')?.scroll({
      top: document.getElementById('bodyContainer')?.scrollHeight, //scroll to the bottom of the element
      behavior: 'smooth', //auto, smooth, initial, inherit
    });
    setImageUrl('');
    //setTimeout(()=>{setIsResponseVisible(true)},250);
  };

  /**
   * method to handle insert response
   * @param response - response from the insert action
   * @example
   * ```
   * insertNBRHandler('response');
   * ```
   */
  const insertNBRHandler = (response: string| any) => {
    if (typeof response !== 'string'){
      console.log('Response URL:', response.url);
      setShouldAddImage(true);
      setImageUrl(response.url);
    }
    else{
    const contentState = ContentState.createFromText(response);
    const newEditorState = EditorState.createWithContent(contentState);
    setEditorState(newEditorState);
    editorRef.current.focusEditor();
    setIsResponseVisible(false);
    //setTimeout(()=>{setIsResponseVisible(true)},250);
    }
  };

  /**
   * funtion to cancel data
   * @example cancelData()
   */
  const cancelData = () => {
    setData('');
    setEditorState(EditorState.createEmpty());
    setIsResponseVisible(true);
  };

  /**
   * Function to render custom button
   * @example renderCustomButton()
   * @returns
   */
  const renderCustomButton = () => {
    return (
      <div className={'sendButtonContainer'}>
        {isLiveCopilotEnabled && (
          <div className={'custumIconWrapper'}>
            <CcfUploadIcon className="attachment" />
            {false && <CcfImageIcon className="alignStyles" />}
            <CcfEmojiIcon style={{ marginTop: '3px' }} />
            {false && <CcfGifIcon className="alignStyles" />}
            <FormatTextdirectionLToR className="alignStyles" />
            <FormatTextdirectionRToL className="alignStyles" />
          </div>
        )}
        <div
          style={{
            width: '100%',
            display: 'flex',
            marginTop: '3px',
            marginLeft: '108px',
            justifyContent: 'flex-end',
          }}
        >
          <div className={'emptyspaces'}></div>
          <div className={'custumButtonWrapper'}>
            {!isLiveCopilotEnabled && (
              <CcfButton
                disabled={!isEnabled}
                className={'quickRepliesButton'}
                onClick={() =>
                  dispatch(updateAppSpaceTab({ tab: AppSpace.QUICK_REPLY }))
                }
                id="agent_webChat_repliesButton"
              >
                <CcfFlashIcon />
                <div className="quickRepliesText">{translate('replies')}</div>
              </CcfButton>
            )}
            {isLiveCopilotEnabled && isEnabled && data.trim() && (
              <Button
                onClick={() => cancelData()}
                size="small"
                id="agent_webChat_cancelButton"
              >
                <CancelOutlined />
              </Button>
            )}
            {!isLiveCopilotEnabled && (
              <CcfButton
                onClick={() => cancelData()}
                className={'quickRepliesButton'}
                id="agent_webChat_cancelButton"
              >
                {translate('cancel')}
              </CcfButton>
            )}
            <CcfButton
              disabled={!(isEnabled && data.trim() || imageUrl !== '')}
              style={{ padding: '7px' }}
              className={
                isEnabled && data?.trim() || imageUrl ? 'sendButton' : 'disableButton'
              }
              onClick={() => sendMessage()}
              id="agent_webChat_sendReplyButton"
            >
              {isLiveCopilotEnabled ? translate('sendMessage') : <Send />}
            </CcfButton>
          </div>
        </div>
      </div>
    );
  };
  /**
   * Function to render upload icon
   * @example renderUploadIcon()
   * @returns
   */
  const renderUploadIcon = () =>
    activeInteraction?.hasAbilityToSendFiles ? (
      <div>
        <CcfUploadIcon className="attachment" />
      </div>
    ) : (
      <div />
    );

  /**
   * Function to set height of interaction post
   * @param event -any
   * @example isEmail
   */
  const formatMsgTime = () => {
    const date = new Date();
    const result = date.toUTCString();
    return result;
  };

  return (
    <>
      {isLiveCopilotEnabled &&
        activecontact?.channel === 'Chat' &&
        isResponseVisible &&
        copilotNbrsFromLLM?.length && (
          <Box>
            {
              <CcfCarousel index={nbrCardIndex} sx={classes.newCarouselStyles}>
                {copilotNbrsFromLLM?.map((response: string, index: number) => (
                  <Box key={index} sx={classes.responseDiv}>
                    <CcfSparklesIcon
                      htmlColor={primaryIconColor}
                      sx={classes.nbrSparkle}
                    />
                    <CcfTypography sx={classes.responseText}>
                      {response}
                    </CcfTypography>
                    <Box sx={classes.responseIcons}>
                      <CcfTooltip title={translate('likeResponse')} arrow>
                        <IconButton sx={classes.nullHover}>
                          {isLikeClicked ? (
                            <CcfCopilotLikedIcon
                              sx={classes.icons}
                              onClick={() => {
                                if(isLikeClicked){
                                  setIsLikeClicked(false);
                                  setIsDisLikeClicked(false);
                                }else{
                                setIsLikeClicked(false); 
                                setIsDisLikeClicked(true);
                                }
                              }}
                              data-testid="likeResponse"
                              fill={theme?.palette?.text?.filter}
                            />
                          ) : (
                            <CcfCopilotLikeIcon
                              sx={classes.icons}
                              onClick={() => {setIsLikeClicked(true); setIsDisLikeClicked(false)}}
                              data-testid="likeResponse"
                              fill={theme?.palette?.text?.filter}
                            />
                          )}
                        </IconButton>
                      </CcfTooltip>
                      <CcfTooltip title={translate('dislikeResponse')} arrow>
                        <IconButton sx={classes.nullHover}>
                          {isDisLikeClicked ? (
                            <CcfCopilotDislikedIcon
                              sx={classes.icons}
                              onClick={() => {
                                if(isDisLikeClicked){
                                  setIsDisLikeClicked(false);
                                  setIsLikeClicked(false);
                                }else{
                                setIsDisLikeClicked(false); 
                                setIsLikeClicked(true)
                              }}}
                              data-testid="dislikeResponse"
                              fill={theme?.palette?.text?.filter}
                            />
                          ) : (
                            <CcfCopilotDislikeIcon
                              sx={classes.icons}
                              onClick={() =>{ setIsDisLikeClicked(true) ; setIsLikeClicked(false)}}
                              data-testid="dislikeResponse"
                              fill={theme?.palette?.text?.filter}
                            />
                          )}
                        </IconButton>
                      </CcfTooltip>
                      <CcfTooltip title={translate('insertResponse')} arrow>
                        <IconButton sx={classes.nullHover}>
                          <CcfInsertIcon
                            sx={classes.icons}
                            onClick={() => insertNBRHandler(response)}
                            data-testid="insert"
                            fill={theme?.palette?.text?.filter}
                          />
                        </IconButton>
                      </CcfTooltip>
                      <CcfTooltip title={translate('sendResponse')} arrow>
                        <IconButton sx={classes.nullHover}>
                          <CcfSendIcon
                            sx={classes.icons}
                            onClick={() => sendMessage(response)}
                            data-testid="send"
                            fill={theme?.palette?.text?.black}
                          />
                        </IconButton>
                      </CcfTooltip>
                    </Box>
                  </Box>
                ))}
              </CcfCarousel>
            }
          </Box>
        )}
      <Editor
        ref={editorRef}
        toolbar={isLiveCopilotEnabled ? noToolBarOptions : toolBarOptions}
        toolbarHidden={false}
        editorState={editorState}
        toolbarClassName={'toolbarClassName'}
        wrapperClassName={styles['wrapperClassName']}
        editorClassName={'editorClassName'}
        onEditorStateChange={onEditorStateChange}
        toolbarCustomButtons={[renderCustomButton()]}
        spellCheck={true}
      />
    </>
  );
}

export default CcfTextEditor;
